import { Overline, Reveal } from "@/lib/reveal";
import roadImage from "@/assets/work-road.jpg";

const SERVICES = [
  {
    n: "01",
    title: "Individual",
    body: "A place to look closely at what you have been carrying—and decide what is still yours to carry.",
  },
  {
    n: "02",
    title: "Couples",
    body: "Thoughtful space for relationships, patterns, questions, and the work of hearing one another differently.",
  },
  {
    n: "03",
    title: "Family",
    body: "A place for family stories, inherited roles, unfinished conversations, and what might become possible next.",
  },
  {
    n: "04",
    title: "Telehealth",
    body: "Online therapy for individuals, couples, and families throughout California.",
  },
];

export function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      aria-labelledby="work-heading"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <Reveal>
          <Overline index="01" label="The Work" />
        </Reveal>
        <div className="mt-8 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <h2
              id="work-heading"
              className="font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-5xl"
            >
              A place to <em className="italic text-haint">look again.</em>
            </h2>
            <div className="mt-8 max-w-md space-y-5 text-base leading-relaxed text-stone/80">
              <p>
                A life gathers meaning slowly—in families and friendships, in
                what we inherit, in what we survive, in the roles we learn to
                play, and in the moments that quietly change our understanding
                of who we are.
              </p>
              <p>
                Therapy offers a place to look again. To notice what repeats. To
                name what has remained unfinished. To understand the ways we
                learned to protect ourselves, and to consider whether those
                protections still belong to the life we are living now.
              </p>
              <p>
                You are the expert in your life. The work is not to become
                someone else, but to know yourself more fully—and to choose,
                with greater freedom, what comes next.
              </p>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h3 className="mb-6 font-serif text-2xl font-light italic text-stone sm:text-3xl">
                Where would you like to begin?
              </h3>
            </Reveal>
            <ul className="border-t border-white/10">
              {SERVICES.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <li
                    data-testid={`work-chapter-${s.n}`}
                    className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-b border-white/10 py-8 transition-colors duration-500 hover:bg-white/[0.02] sm:grid-cols-[auto_0.6fr_1fr] sm:gap-10"
                  >
                    <span className="font-mono text-xs tracking-[0.25em] text-gold/80">
                      {s.n}
                    </span>
                    <h4 className="font-serif text-2xl font-light text-ivory transition-colors duration-300 group-hover:text-haint sm:text-3xl">
                      {s.title}
                    </h4>
                    <p className="col-start-2 mt-2 max-w-lg text-sm leading-relaxed text-stone/75 sm:col-start-3 sm:mt-0 sm:text-base">
                      {s.body}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Reveal className="mt-24">
        <figure className="film-frame" data-testid="work-photograph">
          <img
            src={roadImage}
            alt="Maya, a character in The Inkwell's film At Last, walking an empty highway toward towering storm clouds — a frame from the film"
            loading="lazy"
            className="photo-wash h-[58vh] min-h-[400px] w-full object-cover object-center"
          />
        </figure>
      </Reveal>
    </section>
  );
}
